import { Card, Link, Skeleton } from "@heroui/react";

import { useLocale } from "@/src/i18n/i18n";

type StatTileProps = {
  label: string;
  /** `undefined` tant que le serveur n'a pas répondu — pas `0`. */
  value: number | undefined;
  /** Seconde ligne, déjà traduite. `null` tant qu'on ne la connaît pas. */
  hint: string | null;
  /** L'écran où ce chiffre se détaille. */
  href: string;
};

/**
 * Un chiffre de la plateforme, et la porte vers l'écran qui le détaille.
 *
 * ⚠️ **Toute la tuile est cliquable, mais c'est le libellé qui porte le lien** —
 * même montage que `AccountRow`, `static` compris : `.link` pose
 * `position: relative`, et sans lui l'`after:inset-0` s'ancrerait sur le lien au
 * lieu de la tuile.
 *
 * ⚠️ **Pas de `tabular-nums` sur la valeur.** Les chiffres tabulaires donnent à
 * chacun la largeur d'un `0` : à cette taille, « 121 » s'écarterait visiblement.
 * On les réserve aux colonnes qui doivent s'aligner verticalement — les âges de
 * la file, par exemple.
 */
export function StatTile({ label, value, hint, href }: StatTileProps) {
  const { locale } = useLocale();

  return (
    <Card className="relative flex min-w-0 flex-col gap-1 p-4 transition-colors hover:bg-surface-hover">
      <Link
        className="static text-xs font-medium text-muted after:absolute after:inset-0"
        href={href}
      >
        {label}
      </Link>

      {value === undefined ? (
        <Skeleton className="my-1 h-7 w-20" />
      ) : (
        <p className="text-3xl font-semibold tracking-[-0.02em]">
          {/* Un millier ne s'écrit pas pareil des deux côtés de la Manche :
              `1,284` en anglais, `1 284` en français. */}
          {new Intl.NumberFormat(locale).format(value)}
        </p>
      )}

      {/* Hauteur réservée : sans elle, une tuile dont l'indice n'est pas encore
          connu serait plus courte que ses voisines, et la rangée sauterait à
          l'arrivée de la réponse. */}
      <p className="min-h-4 text-xs text-muted">{hint}</p>
    </Card>
  );
}
